import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronRight, RotateCcw, Search, ShieldCheck } from "lucide-react";
import { API } from "@/lib/site";

const MODE_BADGE = {
  OBD: "bg-regal-amber/10 text-regal-amber",
  BENCH: "bg-sky-50 text-sky-700",
  BOOT: "bg-emerald-50 text-emerald-700",
};

const inputCls = "h-11 w-full border border-line bg-white px-3 text-sm text-ink outline-none focus:border-regal-amber transition-colors";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500";

function FilterSelect({ label, value, onChange, options, testId, placeholder = "All" }) {
  return (
    <div>
      <label className={labelCls} htmlFor={testId}>{label}</label>
      <select id={testId} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function FlexVehicles() {
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState(null);
  const [meta, setMeta] = useState({ total: 0, synced_at: "", year_min: "", year_max: "" });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const PER_PAGE = 20;

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    if (search.trim()) p.set("search", search.trim());
    return p;
  }, [filters, search]);

  const loadOptions = useCallback(() => {
    axios.get(`${API}/flex-vehicles/filters?${params}`).then((r) => {
      setOptions(r.data.options);
      setMeta({ total: r.data.total, synced_at: r.data.synced_at, year_min: r.data.year_min, year_max: r.data.year_max });
    }).catch(() => {});
  }, [params]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams(params);
    p.set("page", page);
    p.set("per_page", PER_PAGE);
    axios.get(`${API}/flex-vehicles?${p}`)
      .then((r) => { setRows(r.data.rows); setCount(r.data.count); })
      .catch(() => { setRows([]); setCount(0); })
      .finally(() => setLoading(false));
  }, [params, page]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const reset = () => { setFilters({}); setSearch(""); setPage(1); };

  const years = useMemo(() => {
    if (!meta.year_min || !meta.year_max) return [];
    const out = [];
    for (let y = parseInt(meta.year_max, 10); y >= parseInt(meta.year_min, 10); y--) out.push(String(y));
    return out;
  }, [meta]);

  const pages = Math.max(Math.ceil(count / PER_PAGE), 1);
  const activeCount = Object.values(filters).filter(Boolean).length + (search.trim() ? 1 : 0);

  return (
    <main data-testid="flex-vehicles-page" className="pt-32 sm:pt-40 pb-24">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-tools/magic-motorsport" className="hover:text-regal-amber">Magicmotorsport FLEX</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">Vehicle List</span>
        </nav>

        <p className="overline-label mb-4">FLEX Compatibility Checker</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink">
          FLEX Vehicle Compatibility List
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
          Search {meta.total ? meta.total.toLocaleString() : ""} vehicle, ECU and TCU applications covered by Magicmotorsport FLEX. Compatibility data comes from the official Magic Motorsport FLEX database and is refreshed regularly — exact support depends on the control unit and software package.
        </p>

        {/* FILTERS */}
        <div className="mt-10 border border-line bg-surface p-6 sm:p-8" data-testid="flex-vehicles-filters">
          <div className="relative mb-6">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search brand, model, engine, engine code or ECU…"
              className={`${inputCls} pl-9`}
              data-testid="fv-search-input"
            />
          </div>
          {options && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FilterSelect label="Vehicle Type" value={filters.vehicle_type || ""} onChange={(v) => setFilter("vehicle_type", v)} options={options.vehicle_type} testId="fv-filter-type" />
              <FilterSelect label="Brand" value={filters.brand || ""} onChange={(v) => { setFilter("brand", v); setFilters((f) => ({ ...f, brand: v, model: "", engine: "" })); }} options={options.brand} testId="fv-filter-brand" />
              <FilterSelect label="Model" value={filters.model || ""} onChange={(v) => setFilter("model", v)} options={options.model} testId="fv-filter-model" />
              <FilterSelect label="Engine" value={filters.engine || ""} onChange={(v) => setFilter("engine", v)} options={options.engine} testId="fv-filter-engine" />
              <FilterSelect label="Fuel" value={filters.fuel || ""} onChange={(v) => setFilter("fuel", v)} options={options.fuel} testId="fv-filter-fuel" />
              <FilterSelect label="Model Year" value={filters.year || ""} onChange={(v) => setFilter("year", v)} options={years} testId="fv-filter-year" />
              <FilterSelect label="ECU Manufacturer" value={filters.ecu_maker || ""} onChange={(v) => setFilter("ecu_maker", v)} options={options.ecu_maker} testId="fv-filter-ecu-maker" />
              <FilterSelect label="MCU Type" value={filters.mcu_type || ""} onChange={(v) => setFilter("mcu_type", v)} options={options.mcu_type} testId="fv-filter-mcu" />
              <FilterSelect label="ECU / TCU Model" value={filters.ecu_name || ""} onChange={(v) => setFilter("ecu_name", v)} options={options.ecu_name} testId="fv-filter-ecu-name" />
              <FilterSelect label="Connection Mode" value={filters.mode || ""} onChange={(v) => setFilter("mode", v)} options={options.modes} testId="fv-filter-mode" />
              <div>
                <label className={labelCls} htmlFor="fv-filter-package">FLEX Software Package</label>
                <select id="fv-filter-package" data-testid="fv-filter-package" value={filters.package || ""} onChange={(e) => setFilter("package", e.target.value)} className={inputCls}>
                  <option value="">All</option>
                  {options.packages.map((p) => <option key={p.sku} value={p.sku}>{p.sku} — {p.description || p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} htmlFor="fv-ps-from">Power PS from</label>
                  <input id="fv-ps-from" data-testid="fv-ps-from" type="number" min="0" value={filters.ps_from || ""} onChange={(e) => setFilter("ps_from", e.target.value)} className={inputCls} placeholder="Min" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="fv-ps-to">Power PS to</label>
                  <input id="fv-ps-to" data-testid="fv-ps-to" type="number" min="0" value={filters.ps_to || ""} onChange={(e) => setFilter("ps_to", e.target.value)} className={inputCls} placeholder="Max" />
                </div>
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid="fv-result-count">
              {loading ? "Searching…" : `${count.toLocaleString()} matching application${count === 1 ? "" : "s"}`}
            </p>
            <div className="flex items-center gap-3">
              {activeCount > 0 && (
                <button onClick={reset} data-testid="fv-reset-button" className="inline-flex items-center gap-2 border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                  <RotateCcw size={13} /> Reset
                </button>
              )}
              <Link to="/tuning-tools/magic-motorsport#flex-products" data-testid="fv-view-packages-cta" className="inline-flex items-center gap-2 bg-regal-amber px-6 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
                View FLEX Packages <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* RESULTS */}
        <div className="mt-8 overflow-x-auto border border-line" data-testid="fv-results">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead>
              <tr className="border-b border-line bg-white">
                {["Vehicle", "Engine", "Fuel", "PS / kW", "Years", "ECU / TCU", "Connection", "SW Packages"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid="fv-empty">No applications match those filters. Try widening your search — or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">ask us to check coverage</Link>.</td></tr>
              )}
              {rows.map((v) => (
                <tr key={v.mm_id} className="border-b border-line bg-white last:border-0 hover:bg-surface/60 transition-colors" data-testid={`fv-row-${v.mm_id}`}>
                  <td className="px-4 py-3.5">
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{v.brand} {v.model}</p>
                    <p className="text-xs text-neutral-500">{v.vehicle_type}{v.version ? ` · ${v.version}` : ""}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-ink">{v.engine}</p>
                    {v.engine_code && <p className="font-mono text-[11px] text-neutral-500">{v.engine_code}</p>}
                  </td>
                  <td className="px-4 py-3.5 text-neutral-400">{v.fuel}</td>
                  <td className="px-4 py-3.5 text-neutral-400">{v.power_ps}{v.power_ps && v.power_kw ? " / " : ""}{v.power_kw}</td>
                  <td className="px-4 py-3.5 text-neutral-400">{v.year_from}{v.year_to && v.year_to !== v.year_from ? `–${v.year_to}` : ""}</td>
                  <td className="px-4 py-3.5">
                    <p className="text-ink">{v.ecu_maker} {v.ecu_name}</p>
                    {v.mcu_type && <p className="font-mono text-[11px] text-neutral-500">{v.mcu_type}</p>}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex flex-wrap gap-1.5">
                      {v.modes.map((m) => (
                        <span key={m} className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${MODE_BADGE[m] || "bg-surface text-neutral-500"}`}>{m.replace("_", " ")}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex max-w-[220px] flex-wrap gap-1.5">
                      {v.packages.slice(0, 4).map((p) => (
                        <span key={p} className="border border-line px-2 py-0.5 font-mono text-[10px] tracking-[0.08em] text-neutral-500">{p}</span>
                      ))}
                      {v.packages.length > 4 && <span className="px-1 py-0.5 font-mono text-[10px] text-neutral-400">+{v.packages.length - 4}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pages > 1 && (
          <div className="mt-6 flex items-center justify-between" data-testid="fv-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="fv-prev" className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Previous</button>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">Page {page} of {pages.toLocaleString()}</p>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="fv-next" className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Next</button>
          </div>
        )}

        {/* BOTTOM CTA */}
        <div className="mt-14 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center" data-testid="fv-bottom-cta">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber"><ShieldCheck size={14} /> Genuine Magic Motorsport data</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">Found your vehicle? Get the right FLEX package.</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">Master and Slave configurations, FLEX Slave options linked to Regal Tuning, and our Build Your FLEX configurator — all with Regal technical support.</p>
          </div>
          <Link to="/tuning-tools/magic-motorsport#flex-products" data-testid="fv-view-packages-cta-bottom" className="inline-flex shrink-0 items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
            View FLEX Packages <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
